import React from 'react';
import { COLORS, TYPOGRAPHY } from '../../lib/styleGuide';

const ACCENT_GRAY = '#757575';
const SUCCESS_GREEN = '#4CAF50';
const WARNING_ORANGE = '#FF9800';

const allowedItems = [
  'Bread',
  'Cookies',
  'Muffins',
  'Cakes',
  'Jams & Jellies',
  'Granola',
  'Dried Fruits',
  'Nuts & Seeds'
];

const restrictedItems = [
  'Items requiring refrigeration',
  'Dairy products',
  'Meat products',
  'Fish & seafood',
  'Eggs (unless in baked goods)'
];

export default function CottageFoodEligibilityStep({ onNext, onBack, isFirst, isLast }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, minHeight: '80vh' }}>
      {/* Header Section */}
      <div>
        <h2 style={{ fontWeight: 700, fontSize: TYPOGRAPHY.ONBOARDING.H2, margin: 0, color: '#222' }}>
          You're eligible to sell from home!
        </h2>
        <p style={{ color: ACCENT_GRAY, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, margin: '10px 0 0 0', lineHeight: 1.5 }}>
          Based on what you're making, you may qualify under your state's Cottage Food Law.
        </p>
      </div>

      {/* Cottage Food Law Explanation */}
              <div style={{
          background: `linear-gradient(135deg, rgba(244, 162, 97, 0.1) 0%, rgba(231, 111, 81, 0.1) 50%, rgba(244, 162, 97, 0.1) 100%)`,
          borderRadius: 12,
          padding: 20,
          border: `1px solid rgba(244, 162, 97, 0.3)`
        }}>
        <div style={{ marginBottom: 12 }}>
          <h3 style={{ fontWeight: 600, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, margin: 0, color: '#222' }}>
            What is Cottage Food Law?
          </h3>
        </div>
        <p style={{ color: '#495057', fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, margin: 0, lineHeight: 1.6 }}>
          Cottage Food Laws allow you to sell certain homemade foods directly to customers without needing a commercial kitchen license. This is perfect for small-scale food creators who want to start selling from home!
        </p>
      </div>

      {/* Allowed Items Section */}
      <div>
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontWeight: 600, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, margin: 0, color: '#222' }}>
            You can sell these items:
          </h3>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 12
        }}>
          {allowedItems.map((item, index) => (
            <div key={index} style={{
              padding: '8px 12px',
              background: '#F8F9FA',
              borderRadius: 8,
              border: '1px solid #E9ECEF'
            }}>
              <span style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, color: '#222', fontWeight: 500 }}>
                {item}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Restricted Items Section */}
      <div>
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontWeight: 600, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, margin: 0, color: '#222' }}>
            These items are not allowed:
          </h3>
        </div>
        <div style={{
          background: '#F8F9FA',
          borderRadius: 12,
          padding: 16,
          border: '1px solid #E9ECEF'
        }}>
          {restrictedItems.map((item, index) => (
            <div key={index} style={{
              marginBottom: index < restrictedItems.length - 1 ? 8 : 0
            }}>
              <span style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, color: '#F44336', fontWeight: 500 }}>
                {item}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Legal Considerations */}
      <div style={{
        background: `linear-gradient(135deg, rgba(244, 162, 97, 0.1) 0%, rgba(231, 111, 81, 0.1) 50%, rgba(244, 162, 97, 0.1) 100%)`,
        borderRadius: 12,
        padding: 16,
        border: `1px solid rgba(244, 162, 97, 0.3)`
      }}>
        <div style={{ marginBottom: 12 }}>
          <h3 style={{ fontWeight: 600, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, margin: 0, color: '#222' }}>
            Important Legal Notes:
          </h3>
        </div>
        <ul style={{ margin: 0, paddingLeft: 20, color: '#222', fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, lineHeight: 1.6, listStyle: 'disc' }}>
          <li>You must follow your state's specific Cottage Food Law requirements</li>
          <li>Annual sales limits may apply (varies by state)</li>
          <li>Proper labeling is required on all products</li>
          <li>You may need to register with your local health department</li>
        </ul>
      </div>

      {/* Navigation Buttons */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        gap: 12, 
        marginTop: 'auto'
      }}>
        {!isFirst && (
          <button
            type="button"
            onClick={onBack}
            style={{
              background: '#fff',
              color: '#9CA3AF',
              border: 'none',
              borderRadius: 10,
              padding: '14px 32px',
              fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
              fontWeight: 600,
              cursor: 'pointer',
              minHeight: 44,
              minWidth: 120,
              transition: 'all 0.2s',
            }}
            aria-label="Back"
          >
            Back
          </button>
        )}
        <button
          type="button"
          onClick={() => onNext && onNext()}
          style={{
            background: COLORS.PRIMARY_GRADIENT,
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            padding: '14px 32px',
            fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
            fontWeight: 600,
            cursor: 'pointer',
            minHeight: 44,
            minWidth: 120,
            transition: 'all 0.2s',
            marginLeft: 'auto'
          }}
          aria-label="Continue"
        >
          Continue
        </button>
      </div>
    </div>
  );
} 